import { useEffect, useMemo, useState } from "react";
import { PlanSelector } from "./components/PlanSelector";
import { FamilyToggle } from "./components/FamilyToggle";
import { AgeInput } from "./components/AgeInput";
import { PremiumResult } from "./components/PremiumResult";
import {
  calcPremium,
  FALLBACK_TABLE,
  isPremiumTable,
  type FamilySize,
  type PremiumTable,
} from "./lib/premiums";
import { buildApplyPayload, postApplyMessage } from "./lib/handoff";
import { startHeightReporter } from "./lib/iframeHeight";
import { OPEN_FORM_IN_NEW_TAB, PREMIUMS_API_PATH } from "./config";

// Slider bounds. Upper bound covers the highest max entry age (Gap 500 = 70);
// ages above a plan's own max entry age surface the "contact us" message.
const AGE_SLIDER_MAX = 70;

export function App() {
  const [table, setTable] = useState<PremiumTable>(FALLBACK_TABLE);
  const [planId, setPlanId] = useState<string>(FALLBACK_TABLE.plans[0].id);
  const [familySize, setFamilySize] = useState<FamilySize>(1);
  const [age, setAge] = useState<number>(40);

  // Report height so the Wix iframe can auto-size.
  useEffect(() => startHeightReporter(), []);

  // Fetch the live premium table from KV (via the Pages Function). On any
  // failure we silently keep the bundled fallback so the calculator still works.
  useEffect(() => {
    let cancelled = false;
    fetch(PREMIUMS_API_PATH, { headers: { accept: "application/json" } })
      .then((r) => (r.ok ? r.json() : Promise.reject(r.status)))
      .then((data) => {
        if (!cancelled && isPremiumTable(data)) {
          setTable(data);
          setPlanId((cur) =>
            data.plans.some((p) => p.id === cur) ? cur : data.plans[0].id,
          );
        }
      })
      .catch(() => {
        /* keep FALLBACK_TABLE */
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const ageMin = table.minEntryAge;
  const result = useMemo(
    () => calcPremium(table, planId, familySize, age),
    [table, planId, familySize, age],
  );

  const selectedPlan = table.plans.find((p) => p.id === planId);
  const canApply = result.status === "ok";

  const handleApply = () => {
    if (result.status !== "ok" || !selectedPlan) return;
    const payload = buildApplyPayload({
      planId: selectedPlan.id,
      planName: selectedPlan.name,
      familySize,
      age,
      band: result.band,
      premium: result.premium,
    });
    // 1) Tell the Wix parent (embedded-Jotform route; payload.prefillUrl).
    postApplyMessage(payload);
    // 2) Open the prefilled Jotform directly.
    if (payload.prefillUrl) {
      if (OPEN_FORM_IN_NEW_TAB) window.open(payload.prefillUrl, "_blank", "noopener");
      else window.location.assign(payload.prefillUrl);
    }
  };

  const applyDisabled = !canApply;

  return (
    <main className="quote">
      <div className="quote__card">
        <header className="quote__header">
          <h1 className="quote__title">
            Your instant <span className="accent">quote</span>
          </h1>
          <p className="quote__subtitle">
            Get your monthly premium in three quick steps.
          </p>
        </header>

        <div className="quote__fields">
          <PlanSelector plans={table.plans} value={planId} onChange={setPlanId} />
          <FamilyToggle value={familySize} onChange={setFamilySize} />
          <AgeInput
            value={age}
            onChange={setAge}
            min={ageMin}
            max={AGE_SLIDER_MAX}
          />
        </div>

        <PremiumResult result={result} currency={table.currency} />

        <button
          type="button"
          className="cta"
          onClick={handleApply}
          disabled={applyDisabled}
        >
          Apply now
        </button>
      </div>
    </main>
  );
}
